/**
 * Fachliche Konstanten der WhatsApp-Business-Cloud-Anbindung. Die Werte spiegeln
 * die Vorgaben der Meta Graph API; Abweichungen sind an der jeweiligen Stelle
 * begründet.
 */
/** Version der Graph API, gegen die dieses Modul entwickelt wurde. */
export const WHATSAPP_GRAPH_API_VERSION = 'v20.0';
export const WHATSAPP_GRAPH_BASE_URL = 'https://graph.facebook.com';
export const WHATSAPP_CONNECTION_STATUSES = ['CONNECTED', 'DISCONNECTED', 'ERROR'];
export const WHATSAPP_CONVERSATION_STATUSES = ['OPEN', 'PENDING', 'RESOLVED', 'ARCHIVED'];
export const WHATSAPP_MESSAGE_DIRECTIONS = ['INBOUND', 'OUTBOUND'];
export const WHATSAPP_MESSAGE_TYPES = [
    'TEXT',
    'IMAGE',
    'DOCUMENT',
    'AUDIO',
    'VIDEO',
    'LOCATION',
    'TEMPLATE',
    'STICKER',
    'INTERACTIVE',
];
export const WHATSAPP_MESSAGE_STATUSES = [
    'PENDING',
    'SENT',
    'DELIVERED',
    'READ',
    'FAILED',
];
export const WHATSAPP_TEMPLATE_CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
export const WHATSAPP_TEMPLATE_STATUSES = [
    'APPROVED',
    'PENDING',
    'REJECTED',
    'PAUSED',
    'DISABLED',
];
export const WHATSAPP_HEADER_TYPES = ['NONE', 'TEXT', 'IMAGE', 'DOCUMENT'];
export const WHATSAPP_OPT_IN_STATUSES = [
    'OPTED_IN',
    'OPTED_OUT',
    'PENDING',
    'NOT_REQUESTED',
];
/** Herkunft der Einwilligung; dient dem DSGVO-Nachweis. */
export const WHATSAPP_OPT_IN_SOURCES = ['WEB_FORM', 'CONTRACT', 'MANUAL', 'IMPORT'];
/**
 * Meta erlaubt freie Nachrichten nur innerhalb von 24 Stunden nach der letzten
 * Kundennachricht. Danach ist ausschließlich eine freigegebene Vorlage zulässig.
 */
export const WHATSAPP_SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;
/** Ab dieser Restlaufzeit weist die Oberfläche auf das nahende Fensterende hin. */
export const WHATSAPP_WINDOW_WARNING_MS = 60 * 60 * 1000;
export const WHATSAPP_MESSAGE_MAX_LENGTH = 4096;
export const WHATSAPP_CAPTION_MAX_LENGTH = 1024;
/** Obergrenze für heruntergeladene Anhänge (Meta liefert maximal 100 MB). */
export const WHATSAPP_MEDIA_MAX_BYTES = 16 * 1024 * 1024;
export const WHATSAPP_CONVERSATION_PAGE_SIZE = 30;
export const WHATSAPP_MESSAGE_PAGE_SIZE = 50;
/** Reihenfolge der Zustellstufen; höhere Stufen überschreiben niedrigere nie rückwärts. */
export const WHATSAPP_STATUS_RANK = {
    PENDING: 0,
    SENT: 1,
    DELIVERED: 2,
    READ: 3,
    FAILED: 4,
};
/**
 * Entscheidet, ob ein eingehendes Status-Update übernommen wird. `FAILED` gilt
 * immer als Endzustand, sonst zählt die höhere Stufe.
 */
export function shouldAdvanceMessageStatus(current, next) {
    if (current === 'FAILED') {
        return false;
    }
    if (next === 'FAILED') {
        return true;
    }
    return WHATSAPP_STATUS_RANK[next] > WHATSAPP_STATUS_RANK[current];
}
/** Verbleibende Zeit des Servicefensters in Millisekunden; 0 bedeutet geschlossen. */
export function serviceWindowRemainingMs(windowExpiresAt, now = new Date()) {
    if (windowExpiresAt === null) {
        return 0;
    }
    const remaining = new Date(windowExpiresAt).getTime() - now.getTime();
    return remaining > 0 ? remaining : 0;
}
export function isServiceWindowOpen(windowExpiresAt, now = new Date()) {
    return serviceWindowRemainingMs(windowExpiresAt, now) > 0;
}
/**
 * ERP-Felder, die als Vorlagenvariable eingesetzt werden können. Die Zuordnung
 * `{{1}} = customer.name` wird je Vorlage gepflegt.
 */
export const WHATSAPP_TEMPLATE_VARIABLE_SOURCES = [
    'customer.name',
    'customer.number',
    'contact.firstName',
    'document.number',
    'document.date',
    'document.dueDate',
    'document.grossTotal',
    'document.currency',
    'tenant.name',
    'user.name',
    'custom',
];
/** Platzhalter der Form {{1}}; Meta nummeriert ab 1 und ohne Lücken. */
export const WHATSAPP_PLACEHOLDER_PATTERN = /\{\{(\d+)\}\}/gu;
export function extractTemplatePlaceholders(text) {
    const found = new Set();
    for (const match of text.matchAll(WHATSAPP_PLACEHOLDER_PATTERN)) {
        const index = Number.parseInt(match[1] ?? '', 10);
        if (Number.isInteger(index) && index > 0) {
            found.add(index);
        }
    }
    return [...found].sort((left, right) => left - right);
}
/** MIME-Typen, die Meta beim Dokumentversand akzeptiert. */
export const WHATSAPP_DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'text/plain',
];
export const WHATSAPP_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Ordnet den Meta-Nachrichtentyp dem internen Enum zu. */
export const WHATSAPP_INBOUND_TYPE_MAP = {
    text: 'TEXT',
    image: 'IMAGE',
    document: 'DOCUMENT',
    audio: 'AUDIO',
    video: 'VIDEO',
    voice: 'AUDIO',
    location: 'LOCATION',
    sticker: 'STICKER',
    interactive: 'INTERACTIVE',
    button: 'INTERACTIVE',
    template: 'TEMPLATE',
};
/** Nachrichtentypen, für die eine Mediendatei geladen werden muss. */
export const WHATSAPP_MEDIA_TYPES = [
    'IMAGE',
    'DOCUMENT',
    'AUDIO',
    'VIDEO',
    'STICKER',
];
export function isMediaMessageType(type) {
    return WHATSAPP_MEDIA_TYPES.includes(type);
}
/** Länge des generierten Webhook-Prüfstrings in Bytes vor der Hex-Kodierung. */
export const WHATSAPP_VERIFY_TOKEN_BYTES = 24;
/** Pfad, den Meta im Developer-Dashboard als Callback-URL erhält. */
export const WHATSAPP_WEBHOOK_PATH = '/api/v1/whatsapp/webhook';
// QR connection attempts are deliberately bounded and never starts from a client poll.
export const WHATSAPP_QR_CAPABILITY_POLL_MS = 15_000;
export const WHATSAPP_QR_SESSION_POLL_MS = 2_000;
export const WHATSAPP_QR_LEASE_MS = 30_000;
export const WHATSAPP_QR_LEASE_REFRESH_MS = 10_000;
export const WHATSAPP_QR_TTL_MS = 60_000;
export const WHATSAPP_QR_RECONNECT_DELAYS_MS = [1_000, 3_000, 10_000];
export const WHATSAPP_QR_CONNECTION_TIMEOUT_MS = 120_000;
export const WHATSAPP_QR_HISTORY_IMPORT_WINDOW_MS = 10 * 60 * 1_000;
export const WHATSAPP_QR_HISTORY_BATCH_SIZE = 50;
export const WHATSAPP_QR_HISTORY_MAX_CONVERSATIONS = 200;
export const WHATSAPP_QR_HISTORY_MAX_PAGES = 20;
export const WHATSAPP_QR_ACK_TTL_MS = 120_000;
export const WHATSAPP_QR_ACK_MAX_PENDING = 1_000;
//# sourceMappingURL=whatsapp.js.map